import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { AutoSizer, InfiniteLoader, Grid } from "react-virtualized";
import PropertyCard from "./PropertyCard";
import apiAgent from "../../../app/api/apiAgent";
import { OMSLoading } from "../../../app/common/common-components/OMSLoading";

const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const PropertyPhotoGrid = ({ searchTerm, ExtraParams }) => {
  const PageSize = 15;

  const [properties, setProperties] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMediumScreen, setIsMediumScreen] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1024);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [requestParams, setRequestParams] = useState(JSON.parse(ExtraParams));



  const fetchProperties = async (params) => {
    setLoading(true);
    try {
      const response = await apiAgent.Property.GetPropertyGridAjax(params);
      setProperties((prev) => (params.page === 1 ? response : [...prev, ...response]));
      setHasMore(response.length >= PageSize);
    } catch (error) {
      console.error("Error fetching property data:", error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (searchTerm, extraParams) => {
      const newParams = {
        ...JSON.parse(extraParams),
        search: searchTerm,
        pageSize: PageSize,
        page: 1,
      };
      setProperties([]);
      setRequestParams(newParams);
      setPage(1);
      await fetchProperties(newParams);
    }, 500),


    []
  );

  useEffect(() => {
    debouncedSearch(searchTerm, ExtraParams);
  }, [searchTerm, ExtraParams]);


  useEffect(() => {
    if (page > 1 && properties.length > 0) {
      fetchProperties({ ...requestParams, page });
    }
  }, [page]);

  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    setIsMobile(width < 768);
    setIsMediumScreen(width >= 768 && width < 1024);
    setIsLargeScreen(width >= 1024);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const isRowLoaded = ({ index }) => index < properties.length;

  const loadMoreRows = () => {
    if (!loading && hasMore) {
      setPage((prev) => prev + 1);
    }
    return Promise.resolve();
  };

  const handleScroll = useCallback(
    ({ scrollTop, clientHeight, scrollHeight }) => {
      if (!loading && hasMore && scrollTop + clientHeight >= scrollHeight - 50) {
        loadMoreRows();
      }
    },
    [loading, hasMore]
  );

  const columns = useMemo(() => (isLargeScreen ? 3 : isMediumScreen ? 2 : 1), [isLargeScreen, isMediumScreen]);

  return (

    <div>
      <OMSLoading loading={loading} />
      <div style={{
        height: "70vh",
        width: "100%",
        overflowY: "auto",
        overflowX: "hidden"
      }}>
        {!loading && properties.length === 0 && (
          <div style={{
            display: "flex",
            height: "70vh",
            padding: "20px",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <h4>No property found matching the provided search criteria. Please refine your search and try again.</h4>
          </div>
        )}


        <AutoSizer>
          {({ height, width }) => (
            <InfiniteLoader isRowLoaded={isRowLoaded} loadMoreRows={loadMoreRows} rowCount={hasMore ? properties.length + 1 : properties.length}>
              {({ onRowsRendered, registerChild }) => (

                properties?.length > 0 && (
                  <Grid
                    className="property-grid"
                    cellRenderer={({ columnIndex, key, rowIndex, style }) => {
                      const index = rowIndex * columns + columnIndex;
                      return index >= properties.length ? (
                        <div key={key} style={{ ...style, height: 0 }}></div>
                      ) : (
                        <div key={key} style={{ ...style, padding: "10px", marginBottom: "10px", boxSizing: "border-box" }}>
                          <PropertyCard property={properties[index]} />
                        </div>
                      );
                    }}
                    columnCount={columns}
                    columnWidth={Math.floor(width / columns)}
                    height={height}
                    rowCount={Math.ceil(properties.length / columns)}
                    rowHeight={140}
                    width={width}
                    overscanRowCount={5}
                    onSectionRendered={onRowsRendered}
                    ref={registerChild}
                    onScroll={handleScroll}
                    style={{ overflowX: "hidden" }}
                  />
                )


              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      </div>
    </div >
  );
};

export default PropertyPhotoGrid;
